import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { useRef, useState, useEffect, useCallback } from "react";
import UpcomingAuditsStatusColumn from "./UpcomingAuditsStatusColumn";
import UpcomingAuditsMinimisedPill from "./UpcomingAuditsMinimizedPill";
import { AuditEntry } from "./UpcomingAuditsCard";
import {
  getCache,
  setCache,
  type ComponentManager,
} from "../../configurations/component-lead/AssignmentsCache";
import apiService from "../../../service/Api";

type AuditStatus = "overdue" | "in_danger" | "safe";
type MobileTab = "summary" | AuditStatus;

interface StaffSummary {
  id: number;
  name: string;
  avatarUrl?: string;
  assignedCount: number;
}

interface UpcomingAuditsProps {
  expanded: boolean;
  onMinimise: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onCountsChange?: (counts: {
    overdue: number;
    inDanger: number;
    safe: number;
  }) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getDaysRemaining = (dateStr: string): number => {
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const getStatus = (dateStr: string): AuditStatus => {
  const days = getDaysRemaining(dateStr);
  if (days < 0) return "overdue";
  if (days <= 3) return "in_danger";
  return "safe";
};

const formatTimeInfo = (dateStr: string): string => {
  const days = getDaysRemaining(dateStr);
  if (days < 0)
    return `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  const due = new Date(dateStr);
  return due.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const getDeveloperName = (dev?: {
  first_name?: string;
  last_name?: string;
  username?: string;
}) => {
  if (!dev) return undefined;
  const full = [dev.first_name, dev.last_name].filter(Boolean).join(" ");
  return full || dev.username;
};

const fetchAllManagers = async (): Promise<ComponentManager[]> => {
  const cached = getCache();
  if (cached) return cached;

  let all: ComponentManager[] = [];
  let url: string | null = "/api/configurations/components-managers/";

  while (url) {
    const res: {
      data: | { results?: ComponentManager[]; next?: string | null } | ComponentManager[];
    } = await apiService.get(url);

    if (Array.isArray(res.data)) {
      all = res.data;
      break;
    } else {
      const paged = res.data as { results?: ComponentManager[]; next?: string | null };
      all = [...all, ...(paged.results ?? [])];
      if (paged.next) {
        const u = new URL(paged.next);
        url = u.pathname + u.search;
      } else {
        url = null;
      }
    }
  }

  setCache(all);
  return all;
};

const mapToAuditEntry = (m: ComponentManager): AuditEntry => ({
  id: m.id,
  componentName: m.component.name,
  assigneeId: m.user_lead?.id,
  assigneeName: getDeveloperName(m.user_lead),
  assigneeAvatar: m.user_lead?.profile_picture_url ?? undefined,
  auditDate: m.next_audit!,
  timeInfo: formatTimeInfo(m.next_audit!),
  status: getStatus(m.next_audit!),
});

export const fetchAuditCounts = async () => {
  const managers = await fetchAllManagers();
  const withAudit = managers.filter((m) => !!m.next_audit);
  return {
    overdue: withAudit.filter((m) => getStatus(m.next_audit!) === "overdue").length,
    inDanger: withAudit.filter((m) => getStatus(m.next_audit!) === "in_danger").length,
    safe: withAudit.filter((m) => getStatus(m.next_audit!) === "safe").length,
  };
};

// ─── Mobile tabs config ───────────────────────────────────────────────────────

const MOBILE_TABS: { key: MobileTab; label: string; color: string }[] = [
  { key: "summary", label: "Summary", color: "#94a3b8" },
  { key: "overdue", label: "Overdue", color: "#E11D48" },
  { key: "in_danger", label: "In Danger", color: "#f59e0b" },
  { key: "safe", label: "Safe", color: "#10b981" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const UpcomingAudits = ({
  expanded,
  onMinimise,
  onPrev,
  onCountsChange,
}: UpcomingAuditsProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);

  const [mobileTab, setMobileTab] = useState<MobileTab>("summary");
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState<AuditEntry | null>(null);

  const loadAudits = useCallback(async () => {
    setLoading(true);
    try {
      const managers = await fetchAllManagers();
      const withAudit = managers.filter((m) => !!m.next_audit);
      setEntries(withAudit.map(mapToAuditEntry));
    } catch (err) {
      console.error("Failed to fetch audit data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAudits();
  }, [loadAudits]);

  useEffect(() => {
    const handleUpdate = () => loadAudits();
    window.addEventListener("assignments-updated", handleUpdate);
    return () => window.removeEventListener("assignments-updated", handleUpdate);
  }, [loadAudits]);


  const grouped = {
    overdue: entries.filter((e) => e.status === "overdue"),
    in_danger: entries.filter((e) => e.status === "in_danger"),
    safe: entries.filter((e) => e.status === "safe"),
  };

  const overdueCount = grouped.overdue.length;
  const inDangerCount = grouped.in_danger.length;
  const safeCount = grouped.safe.length;

  useEffect(() => {
    onCountsChange?.({ overdue: overdueCount, inDanger: inDangerCount, safe: safeCount });
  }, [overdueCount, inDangerCount, safeCount, onCountsChange]);

  const staffMap = new Map<number, StaffSummary>();
  entries.forEach((e) => {
    if (!e.assigneeId) return;
    if (!staffMap.has(e.assigneeId)) {
      staffMap.set(e.assigneeId, {
        id: e.assigneeId,
        name: e.assigneeName ?? "Unknown",
        avatarUrl: e.assigneeAvatar,
        assignedCount: 0,
      });
    }
    staffMap.get(e.assigneeId)!.assignedCount += 1;
  });
  const staffList = Array.from(staffMap.values());

  if (!expanded) {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-end", alignSelf: "stretch" }}>
        <UpcomingAuditsMinimisedPill
          overdueCount={overdueCount}
          inDangerCount={inDangerCount}
          onExpand={onMinimise}
        />
      </Box>
    );
  }

  // ─── Shared top nav ───────────────────────────────────────────────────────
  const TopNav = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <IconButton
        onClick={() => onPrev?.()}
        sx={{
          backgroundColor: colors.primary[400],
          color: "white",
          width: 32,
          height: 32,
          "&:hover": { backgroundColor: colors.primary[300] },
        }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
      </IconButton>

      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9rem",
          color: colors.primary[100],
          minWidth: { xs: 140, sm: 180 },
          textAlign: "center",
        }}
      >
        Upcoming Audits
      </Typography>

      <IconButton
        sx={{
          backgroundColor: colors.primary[800],
          color: colors.primary[100],
          width: 32,
          height: 32,
          border: `1px solid ${colors.primary[300]}30`,
        }}
      >
        <ChevronRight sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  // ─── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    const activeTabConfig = MOBILE_TABS.find((t) => t.key === mobileTab)!;
    const tabCountMap: Record<MobileTab, number> = {
      summary: entries.length,
      overdue: overdueCount,
      in_danger: inDangerCount,
      safe: safeCount,
    };
    const mobileItems = mobileTab !== "summary" ? grouped[mobileTab as AuditStatus] ?? [] : [];

    return (
      <Box sx={{ width: "100%", px: 1 }}>
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "24px",
            p: 2,
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          {/* Minimise */}
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={onMinimise}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: 12,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowDown sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {TopNav}

          {/* Tab strip */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mb: 2,
              backgroundColor: colors.primary[800],
              borderRadius: "14px",
              p: 0.5,
            }}
          >
            {MOBILE_TABS.map((tab) => {
              const isActive = mobileTab === tab.key;
              const count = tabCountMap[tab.key];
              return (
                <Box
                  key={tab.key}
                  onClick={() => setMobileTab(tab.key)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.3,
                    py: 1,
                    px: 0.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: isActive ? colors.primary[900] : "transparent",
                    transition: "background-color 0.2s ease",
                    borderBottom: isActive ? `2px solid ${tab.color}` : "2px solid transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? colors.primary[400] : colors.primary[700],
                      lineHeight: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </Typography>
                  {tab.key !== "summary" && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: isActive ? tab.color : colors.primary[700],
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Tab content */}
          {mobileTab === "summary" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Status counts — tappable to jump to tab */}
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 0.5,
                  }}
                >
                  STATUS OVERVIEW
                </Typography>
                {[
                  { label: "Over Due", count: overdueCount, color: "#E11D48", tab: "overdue" as MobileTab },
                  { label: "In Danger", count: inDangerCount, color: "#f59e0b", tab: "in_danger" as MobileTab },
                  { label: "Safe", count: safeCount, color: "#10b981", tab: "safe" as MobileTab },
                ].map(({ label, count, color, tab }) => (
                  <Box
                    key={label}
                    onClick={() => setMobileTab(tab)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      py: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: "0.85rem", color: colors.primary[100] }}>
                        {label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={count}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: color + "20",
                          color: color,
                          "& .MuiChip-label": { px: 1.2 },
                        }}
                      />
                      <ChevronRight sx={{ fontSize: 14, color: colors.primary[600] }} />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* User leads */}
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  AUDIT USER LEADS
                </Typography>
                {staffList.length === 0 ? (
                  <Typography sx={{ fontSize: "0.75rem", color: colors.primary[400] }}>
                    No assignments yet
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    {staffList.map((staff) => (
                      <Tooltip
                        key={staff.id}
                        title={`${staff.name}: ${staff.assignedCount} audit${staff.assignedCount !== 1 ? "s" : ""}`}
                        arrow
                      >
                        <Box sx={{ position: "relative", cursor: "pointer" }}>
                          <Avatar
                            src={staff.avatarUrl}
                            sx={{ width: 44, height: 44, border: `2px solid ${colors.primary[700]}` }}
                          >
                            {staff.name[0]}
                          </Avatar>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -3,
                              right: -3,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: "#E11D48",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${colors.primary[900]}`,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "0.6rem", color: "white", fontWeight: 700, lineHeight: 1 }}
                            >
                              {staff.assignedCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "60vh",
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                pb: 1,
              }}
            >
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      height: 64,
                      borderRadius: "16px",
                      backgroundColor: colors.primary[800],
                      opacity: 0.5 + i * 0.15,
                    }}
                  />
                ))
              ) : mobileItems.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: activeTabConfig.color + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: activeTabConfig.color,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: "0.8rem", color: colors.primary[400] }}>
                    No {activeTabConfig.label.toLowerCase()} audits
                  </Typography>
                </Box>
              ) : (
                mobileItems.map((entry) => (
                  <Box
                    key={entry.id}
                    onClick={() => setModalEntry(entry)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "16px",
                      backgroundColor: colors.primary[800],
                      cursor: "pointer",
                      borderLeft: `3px solid ${activeTabConfig.color}`,
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: colors.primary[700] + "60" },
                    }}
                  >
                    <Avatar
                      src={entry.assigneeAvatar}
                      sx={{ width: 40, height: 40, flexShrink: 0, border: `1.5px solid ${colors.primary[400]}` }}
                    >
                      {entry.assigneeName?.[0] ?? "?"}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: colors.primary[100],
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {entry.componentName}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: "0.7rem", color: colors.primary[700] }}>
                          {entry.assigneeName ?? "Unassigned"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: activeTabConfig.color,
                          }}
                        >
                          {entry.timeInfo}
                        </Typography>
                      </Box>
                    </Box>
                    <ChevronRight sx={{ fontSize: 16, color: colors.primary[700], flexShrink: 0 }} />
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>

        <Dialog
          open={!!modalEntry}
          onClose={() => setModalEntry(null)}
          PaperProps={{
            sx: {
              borderRadius: "20px",
              backgroundColor: colors.primary[900],
              border: `1px solid ${colors.primary[300]}30`,
              minWidth: 300,
              mx: 2,
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: colors.primary[100] }}>
              Audit Details
            </Typography>
            <IconButton size="small" onClick={() => setModalEntry(null)}>
              <Close sx={{ fontSize: 16, color: colors.primary[600] }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
            {modalEntry && (
              <>
                <Box>
                  <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.5, letterSpacing: "0.08em" }}>
                    COMPONENT
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.primary[100] }}>
                    {modalEntry.componentName}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.8, letterSpacing: "0.08em" }}>
                    USER LEAD
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar src={modalEntry.assigneeAvatar} sx={{ width: 36, height: 36, border: `1.5px solid ${colors.primary[400]}` }}>
                      {modalEntry.assigneeName?.[0] ?? "?"}
                    </Avatar>
                    <Typography sx={{ fontSize: "0.85rem", color: colors.primary[100] }}>
                      {modalEntry.assigneeName ?? "Unassigned"}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.5, letterSpacing: "0.08em" }}>
                    AUDIT DATE
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      color:
                        modalEntry.status === "overdue" ? "#E11D48"
                        : modalEntry.status === "in_danger" ? "#f59e0b"
                        : "#10b981",
                    }}
                  >
                    {modalEntry.timeInfo}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.5, letterSpacing: "0.08em" }}>
                    STATUS
                  </Typography>
                  <Chip
                    label={
                      modalEntry.status === "overdue" ? "Overdue"
                      : modalEntry.status === "in_danger" ? "In Danger"
                      : "Safe"
                    }
                    size="small"
                    sx={{
                      backgroundColor:
                        modalEntry.status === "overdue" ? "#E11D4820"
                        : modalEntry.status === "in_danger" ? "#f59e0b20"
                        : "#10b98120",
                      color:
                        modalEntry.status === "overdue" ? "#E11D48"
                        : modalEntry.status === "in_danger" ? "#f59e0b"
                        : "#10b981",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // Desktop layout 
  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: "calc(100vw - 32px)", md: "fit-content" },
          mx: "auto",
        }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: { xs: "24px", md: "40px" },
            p: { xs: 1.5, md: 3 },
            boxShadow: "0 6px 28px rgba(2, 2, 2, 0.06)",
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          {/* Minimise button */}
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={onMinimise}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 16,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowDown sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {TopNav}

          {/* Scroll container */}
          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {/* Summary panel */}
            <Box
              sx={{
                minWidth: { xs: 200, sm: 240, md: 260 },
                maxWidth: { xs: 200, sm: 240, md: 260 },
                backgroundColor: colors.primary[800],
                borderRadius: "30px",
                p: 2.5,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.1em", color: colors.primary[700] }}
              >
                UPCOMING AUDITS
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                {[
                  { label: "Over Due", count: overdueCount, color: "#E11D48" },
                  { label: "In Danger", count: inDangerCount, color: "#f59e0b" },
                  { label: "SAFE", count: safeCount, color: "#10b981" },
                ].map(({ label, count, color }) => (
                  <Box
                    key={label}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.8rem", color: colors.primary[100] }}>{label}</Typography>
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: colors.primary[700] + "30",
                        color: colors.primary[100],
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* User leads */}
              <Box sx={{ borderRadius: "20px", backgroundColor: colors.primary[900], px: 1.5, py: 2 }}>
                <Typography sx={{ fontSize: "0.75rem", color: colors.primary[700], mb: 1.5 }}>
                  Audit user leads
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {staffList.length === 0 ? (
                    <Typography sx={{ fontSize: "0.75rem", color: colors.primary[700] }}>
                      No assignments yet
                    </Typography>
                  ) : (
                    staffList.map((staff) => (
                      <Tooltip
                        key={staff.id}
                        title={`${staff.name}: ${staff.assignedCount} audit${staff.assignedCount !== 1 ? "s" : ""}`}
                        arrow
                      >
                        <Box sx={{ position: "relative", cursor: "pointer" }}>
                          <Avatar
                            src={staff.avatarUrl}
                            sx={{ width: 40, height: 40, border: `2px solid ${colors.primary[400]}` }}
                          >
                            {staff.name[0]}
                          </Avatar>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -4,
                              right: -4,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: "#E11D48",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${colors.primary[900]}`,
                            }}
                          >
                            <Typography sx={{ fontSize: "0.6rem", color: "white", fontWeight: 700, lineHeight: 1 }}>
                              {staff.assignedCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    ))
                  )}
                </Box>
              </Box>
            </Box>

            {/* Status columns */}
            {(["overdue", "in_danger", "safe"] as AuditStatus[]).map((status) => (
              <UpcomingAuditsStatusColumn
                key={status}
                status={status}
                entries={loading ? [] : grouped[status]}
                colors={colors}
                loading={loading}
                onCardClick={(entry) => setModalEntry(entry)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Detail Modal */}
      <Dialog
        open={!!modalEntry}
        onClose={() => setModalEntry(null)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: colors.primary[900],
            border: `1px solid ${colors.primary[300]}30`,
            minWidth: 340,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: colors.primary[100] }}>
            Audit Details
          </Typography>
          <IconButton size="small" onClick={() => setModalEntry(null)}>
            <Close sx={{ fontSize: 16, color: colors.primary[600] }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {modalEntry && (
            <>
              <Box>
                <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.5, letterSpacing: "0.08em" }}>
                  COMPONENT
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.primary[100] }}>
                  {modalEntry.componentName}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.8, letterSpacing: "0.08em" }}>
                  USER LEAD
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar src={modalEntry.assigneeAvatar} sx={{ width: 36, height: 36, border: `1.5px solid ${colors.primary[400]}` }}>
                    {modalEntry.assigneeName?.[0] ?? "?"}
                  </Avatar>
                  <Typography sx={{ fontSize: "0.85rem", color: colors.primary[100] }}>
                    {modalEntry.assigneeName ?? "Unassigned"}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.5, letterSpacing: "0.08em" }}>
                  AUDIT DATE
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color:
                      modalEntry.status === "overdue" ? "#E11D48"
                      : modalEntry.status === "in_danger" ? "#f59e0b"
                      : "#10b981",
                  }}
                >
                  {modalEntry.timeInfo}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.7rem", color: colors.primary[600], mb: 0.5, letterSpacing: "0.08em" }}>
                  STATUS
                </Typography>
                <Chip
                  label={
                    modalEntry.status === "overdue" ? "Overdue"
                    : modalEntry.status === "in_danger" ? "In Danger"
                    : "Safe"
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      modalEntry.status === "overdue" ? "#E11D4820"
                      : modalEntry.status === "in_danger" ? "#f59e0b20"
                      : "#10b98120",
                    color:
                      modalEntry.status === "overdue" ? "#E11D48"
                      : modalEntry.status === "in_danger" ? "#f59e0b"
                      : "#10b981",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UpcomingAudits;